import React, { useState, useEffect } from "react";
import "./MonthlyVisualization.css";
import { Box, IconButton, Divider } from "@mui/material";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { selectUniqueMonths } from "../app/dataSelectors";
import { useSelector } from "react-redux";
import IconVisualizationCard, { HtmlTooltip } from "./IconVisualizationCard";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const MonthlyVisualization = ({ data, indicators, programLabel }) => {
  const [selectedMonthIndex, setSelectedMonthIndex] = useState(-1); 
  const [loading, setLoading] = useState(true); 

  const sortedUniqueMonths = useSelector((state) =>
    selectUniqueMonths(state).sort((a, b) => a - b)
  );

  useEffect(() => {
    if (sortedUniqueMonths.length > 0) {
      setSelectedMonthIndex(sortedUniqueMonths.length - 1); 
      setLoading(false); 
    }
  }, [sortedUniqueMonths]);

  const handlePrevMonth = () => {
    if (selectedMonthIndex > 0) {
      setSelectedMonthIndex(selectedMonthIndex - 1);
    }
  };

  const handleNextMonth = () => {
    if (selectedMonthIndex < sortedUniqueMonths.length - 1) {
      setSelectedMonthIndex(selectedMonthIndex + 1);
    }
  };

  const getSelectedMonth = () => {
    if (selectedMonthIndex !== -1 && sortedUniqueMonths.length > 0) {
      return new Date(sortedUniqueMonths[selectedMonthIndex]).toLocaleDateString(undefined, { year: "numeric", month: "long" });
    }
    return "Loading..."; 
  };

  const monthData = data.filter((item) => {
    if (selectedMonthIndex !== -1 && sortedUniqueMonths.length > 0) {
      const selectedMonth = new Date(sortedUniqueMonths[selectedMonthIndex]).toLocaleDateString(undefined, { year: "numeric", month: "long" });
      const itemDate = new Date(item.Month).toLocaleDateString(undefined, { year: "numeric", month: "long" });
      return itemDate === selectedMonth;
    }
    return false; 
  });

  const previousMonthIndex = selectedMonthIndex - 1;
  const previousMonth = previousMonthIndex >= 0 && sortedUniqueMonths.length > 0
    ? new Date(sortedUniqueMonths[previousMonthIndex]).toLocaleDateString(undefined, { year: "numeric", month: "long" })
    : null;

  const previousMonthData = previousMonth
    ? data.filter((item) => {
        const itemDate = new Date(item.Month).toLocaleDateString(undefined, { year: "numeric", month: "long" });
        return itemDate === previousMonth;
      })
    : [];

  const calculatePercentageChange = (currentValue, previousValue) => {
    if (previousValue === 0) return 0;
    return ((currentValue - previousValue) / previousValue) * 100;
  };

  const getPercentageChange = (label, currentData, previousData) => {
    const currentValue = currentData[label]
      ? parseFloat(currentData[label])
      : null;
    const previousValue = previousData[label]
      ? parseFloat(previousData[label])
      : null;
    if (!currentValue || !previousValue) {
      return "-";
    }
    return calculatePercentageChange(currentValue, previousValue).toFixed(0);
  };

  return (
    <Box className="visualization-container">
      <Box className="month-selector">
        <IconButton
          onClick={handlePrevMonth}
          disabled={selectedMonthIndex <= 0 || loading}
        >
          <ArrowLeftIcon sx={{ fontSize: 40 }} />
        </IconButton>
        <IconButton
          onClick={handleNextMonth}
          disabled={selectedMonthIndex === sortedUniqueMonths.length - 1 || loading}
        >
          <ArrowRightIcon sx={{ fontSize: 40 }} />
        </IconButton>
        <p className="month-selected-text">{getSelectedMonth()}</p>
        <HtmlTooltip title={"Click on the arrows to select the month you’re interested in."} placement="right-start">
          <InfoOutlinedIcon className="time-period-tooltip"/>
        </HtmlTooltip>
      </Box>

      <Divider />

      {programLabel === "Democracy Program" && (
        <Box className="visualization-description">
          <p className="headline-title">STAR Dashboard</p>
          <p className="headline-body">
            Welcome to the STAR Dashboard! Here you will find monthly updates on
            key CRP and DP indicators. This Dashboard highlights state-level
            data to give an overview of local trends, focal points and other
            valuable insights.
          </p>
        </Box>
      )}

      <div className="cards-container">
        {monthData.map((item, index) =>
          indicators.map((indicator, keyIndex) => {
            const percentageChange =
              previousMonthData.length > 0
                ? getPercentageChange(
                    indicator.label,
                    item,
                    previousMonthData[0]
                  )
                : "-";
            return (
              <IconVisualizationCard
                key={index + "-" + keyIndex}
                label={indicator.label}
                iconName={indicator.icon}
                item={item}
                percentageChange={percentageChange}
                description={indicator.description}
              />
            );
          })
        )}
      </div>
    </Box>
  );
};

export default MonthlyVisualization;
